import React, { Suspense, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "../../../../redux/reducers/global/modal/modalSlice";

import {
  fetchDiagrams,
  selectDiagrams,
  getFilteredDiagramsByText,
  moveDiagramToFolder,
} from "../../../../redux/reducers/global/diagram/diagramSlice";

import {
  fetchFolders,
  selectFolders,
} from "../../../../redux/reducers/global/folder/folderSlice";
import "./style.css";
import { tutorialCollections } from "./tutorialContent";

const DiagramComponent = React.lazy(() => import("./DiagramComponent"));
const FolderComponent = React.lazy(() => import("./FolderComponent"));

export const MainGallery = ({ searchTerm = "" }) => {
  const dispatch = useDispatch();
  const [selectedFolderFilter, setSelectedFolderFilter] = useState(null);

  useEffect(() => {
    dispatch(fetchDiagrams());
    dispatch(fetchFolders());
  }, [dispatch]);

  const allDiagrams = useSelector(selectDiagrams);
  const allFolders = useSelector(selectFolders);
  const diagramLoading = useSelector((state) => state.diagram.diagramLoading);
  const folderLoading = useSelector((state) => state.folder.folderLoading);
  const diagramError = useSelector((state) => state.diagram.diagramError);
  const folderError = useSelector((state) => state.folder.folderError);

  const filteredDiagrams = useSelector((state) =>
    getFilteredDiagramsByText(state, searchTerm)
  );

  const filteredFolders = useMemo(() => {
    if (!searchTerm.trim()) {
      return allFolders || [];
    }
    const lowered = searchTerm.toLowerCase();
    return (allFolders || []).filter((folder) =>
      folder.label?.toLowerCase().includes(lowered)
    );
  }, [allFolders, searchTerm]);

  const selectedFolder = useMemo(() => {
    if (!selectedFolderFilter) {
      return null;
    }
    return (
      (allFolders || []).find((folder) => folder.id === selectedFolderFilter) ||
      null
    );
  }, [selectedFolderFilter, allFolders]);

  const rootFolders = useMemo(() => {
    if (selectedFolderFilter) {
      return [];
    }
    return filteredFolders || [];
  }, [filteredFolders, selectedFolderFilter]);

  const rootDiagrams = useMemo(() => {
    if (selectedFolderFilter) {
      return [];
    }
    return (filteredDiagrams || []).filter((diagram) => !diagram.folder);
  }, [filteredDiagrams, selectedFolderFilter]);

  const diagramsInSelectedFolder = useMemo(() => {
    if (!selectedFolderFilter) {
      return [];
    }
    const source = searchTerm.trim() ? filteredDiagrams || [] : allDiagrams || [];
    return source
      .filter((diagram) => diagram.folder === selectedFolderFilter)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [selectedFolderFilter, allDiagrams, filteredDiagrams, searchTerm]);

  const hasFolders = rootFolders.length > 0;
  const hasDiagrams = rootDiagrams.length > 0;
  const showEmptyState = !selectedFolderFilter && !hasFolders && !hasDiagrams;

  const openNewDiagramModal = () => {
    dispatch(
      openModal({
        modalType: "NEW_DIAGRAM",
        modalProps: {},
      })
    );
  };

  const openNewFolderModal = () => {
    dispatch(
      openModal({
        modalType: "NEW_FOLDER",
        modalProps: {},
      })
    );
  };

  const clearFolderFilter = () => {
    setSelectedFolderFilter(null);
  };

  const handleRootDrop = (event) => {
    event.preventDefault();
    const diagramId = event.dataTransfer.getData("diagramId");
    if (diagramId) {
      dispatch(moveDiagramToFolder({ diagramId, folderId: null }));
    }
  };

  const allowDrop = (event) => {
    event.preventDefault();
  };

  const handleOpenTutorial = (tutorialId) => {
    const tutorial = tutorialCollections.find((item) => item.id === tutorialId);
    if (!tutorial) return;
    dispatch(
      openModal({
        modalType: "KNOWLEDGE_TUTORIAL",
        modalProps: {
          slides: tutorial.slides,
          initialIndex: 0,
        },
      })
    );
  };

  if (diagramLoading || folderLoading) {
    return <div className="gallery-loading">Cargando...</div>;
  }

  if (diagramError || folderError) {
    return (
      <div className="gallery-loading">
        <div>No se pudieron cargar tus diseños.</div>
        <button
          type="button"
          className="gallery-loading__retry"
          onClick={() => {
            dispatch(fetchDiagrams());
            dispatch(fetchFolders());
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="gallery-layout">
      <aside className="gallery-sidebar">
        <button
          type="button"
          className="sidebar-button sidebar-button--primary"
          onClick={openNewDiagramModal}
        >
          <span className="sidebar-button__icon">＋</span>
          <span className="sidebar-button__label">Nuevo diagrama</span>
        </button>
        <button
          type="button"
          className="sidebar-button"
          onClick={openNewFolderModal}
        >
          <span className="sidebar-button__icon sidebar-button__icon--folder">
            <img src="/folderBlanco.png" alt="Crear folder" />
          </span>
          <span className="sidebar-button__label">Crear folder</span>
        </button>
      </aside>
      <div className="gallery-main">
        {selectedFolder ? (
          <>
            <div className="gallery-breadcrumbs">
              <button
                type="button"
                className="breadcrumb-root"
                onClick={clearFolderFilter}
                onDrop={handleRootDrop}
                onDragOver={allowDrop}
              >
                ← Volver
              </button>
              <span className="gallery-breadcrumbs__separator">›</span>
              <span className="gallery-breadcrumbs__current">
                {selectedFolder.label}
              </span>
            </div>
            <div className="gallery-section-title">Diagramas</div>
            <div
              className={`gallery-grid ${
                diagramsInSelectedFolder.length === 0
                  ? "gallery-grid--empty"
                  : ""
              }`}
            >
              {diagramsInSelectedFolder.length > 0 ? (
                diagramsInSelectedFolder.map((diagram) => (
                  <Suspense key={diagram.id} fallback={<div>Loading...</div>}>
                    <DiagramComponent diagram={diagram} />
                  </Suspense>
                ))
              ) : (
                <div className="gallery-empty">
                  <h3>No encontramos resultados</h3>
                  <p>Ajusta tu búsqueda o crea un nuevo elemento.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {hasFolders && (
              <section className="gallery-section">
                <div className="gallery-section-title">Folders</div>
                <div className="gallery-grid">
                  {rootFolders.map((folder) => (
                    <Suspense
                      key={folder.id}
                      fallback={<div>Loading...</div>}
                    >
                      <FolderComponent
                        folder={folder}
                        onFolderClick={(folderId) =>
                          setSelectedFolderFilter(folderId)
                        }
                      />
                    </Suspense>
                  ))}
                </div>
              </section>
            )}

            {hasDiagrams && (
              <section className="gallery-section">
                <div className="gallery-section-title">Diagramas</div>
                <div className="gallery-grid">
                  {rootDiagrams.map((diagram) => (
                    <Suspense
                      key={diagram.id}
                      fallback={<div>Loading...</div>}
                    >
                      <DiagramComponent diagram={diagram} />
                    </Suspense>
                  ))}
                </div>
              </section>
            )}

            {showEmptyState && (
              <div className="gallery-empty">
                <h3>Empieza creando un nuevo diagrama</h3>
              </div>
            )}
          </>
        )}

        <section className="knowledge-base">
          <header className="knowledge-base__header">
            <div className="knowledge-base__title">
              <span className="knowledge-base__icon">📘</span>
              <span>Centro de conocimiento</span>
            </div>
          </header>
          <div className="knowledge-base__content">
            <div className="knowledge-base__items">
              {tutorialCollections.map((tutorial) => (
                <button
                  key={tutorial.id}
                  type="button"
                  className="knowledge-card"
                  onClick={() => handleOpenTutorial(tutorial.id)}
                >
                  <div className="knowledge-card__thumbnail">
                    {tutorial.thumbnail ? (
                      <img src={tutorial.thumbnail} alt={tutorial.cardTitle} />
                    ) : (
                      <div
                        className="knowledge-card__thumbnail-fallback"
                        style={{ background: tutorial.accent }}
                      />
                    )}
                  </div>
                  <div className="knowledge-card__body">
                    <span className="knowledge-card__eyebrow">{tutorial.label}</span>
                    <h4>{tutorial.cardTitle}</h4>
                    <p>{tutorial.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MainGallery;
